
"use client";

import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import {
  doc,
  getDoc,
  setDoc,
  Timestamp,
} from "firebase/firestore";

import { fireDB } from "../../../Backend/Firebase/FirebaseConfigData";

import { toast, ToastContainer } from "react-toastify";

import { motion, AnimatePresence } from "framer-motion";

import "react-toastify/dist/ReactToastify.css";

// CKEditor 5
import BlogEditor from "./BlogEditor";

// =====================================================
// TYPES
// =====================================================

interface SectionData {
  sectionTitle: string;
  content: string;
}

interface EditorSection extends SectionData {
  localId: string;
}

interface ChapterData {
  chapterSlug: string;
  chapterSlugManual?: boolean;

  contentTitle: string;

  title: string;
  titleManual?: boolean;

  content: string;

  author: string;

  seoTitle: string;
  seoDescription: string;
  seoTags: string;

  seoHeading: string;
  seoHeadingManual?: boolean;

  canonicalUrl: string;

  createdAt: string;

  sections: EditorSection[];
}

// =====================================================
// INITIAL DATA
// =====================================================

const createInitialData = (): ChapterData => ({
  chapterSlug: "",
  chapterSlugManual: false,

  contentTitle: "",

  title: "",
  titleManual: false,

  content: "",

  author: "",

  seoTitle: "",
  seoDescription: "",
  seoTags: "",

  seoHeading: "",
  seoHeadingManual: false,

  canonicalUrl: "",

  createdAt: new Date().toISOString(),

  sections: [],
});

// =====================================================
// HELPERS
// =====================================================

const slugify = (value: string): string =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "");

const createSection = (): EditorSection => ({
  localId: crypto.randomUUID(),
  sectionTitle: "",
  content: "",
});

// =====================================================
// COMPONENT
// =====================================================

export default function CreateChapter() {
  const { librarySlug, bookSlug } = useParams<{
    librarySlug: string;
    bookSlug: string;
  }>();

  const [chapterData, setChapterData] =
    useState<ChapterData>(createInitialData);

  const [saving, setSaving] = useState(false);

  // =====================================================
  // AUTO GENERATE FIELDS
  // =====================================================

  useEffect(() => {
    const title = chapterData.contentTitle.trim();

    const generatedSlug = slugify(title);

    setChapterData((prev) => {
      const finalSlug = prev.chapterSlugManual
        ? prev.chapterSlug
        : generatedSlug;

      const finalTitle = prev.titleManual
        ? prev.title
        : title;

      const finalHeading = prev.seoHeadingManual
        ? prev.seoHeading
        : title;

      const canonicalUrl =
        librarySlug && bookSlug && finalSlug
          ? `https://greatmastersai.com/library/${encodeURIComponent(
              librarySlug
            )}/${encodeURIComponent(
              bookSlug
            )}/${encodeURIComponent(finalSlug)}`
          : "";

      if (
        prev.chapterSlug === finalSlug &&
        prev.title === finalTitle &&
        prev.seoHeading === finalHeading &&
        prev.canonicalUrl === canonicalUrl
      ) {
        return prev;
      }

      return {
        ...prev,
        chapterSlug: finalSlug,
        title: finalTitle,
        seoHeading: finalHeading,
        canonicalUrl,
      };
    });
  }, [
    chapterData.contentTitle,
    chapterData.chapterSlug,
    chapterData.chapterSlugManual,
    librarySlug,
    bookSlug,
  ]);

  // =====================================================
  // UPDATE FIELD
  // =====================================================

  const updateField = (
    field: keyof ChapterData,
    value: string
  ) => {
    setChapterData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // =====================================================
  // ADD SECTION
  // =====================================================

  const addSection = () => {
    setChapterData((prev) => ({
      ...prev,
      sections: [
        ...prev.sections,
        createSection(),
      ],
    }));
  };

  // =====================================================
  // UPDATE SECTION
  // =====================================================

  const updateSection = (
    sectionId: string,
    field: keyof SectionData,
    value: string
  ) => {
    setChapterData((prev) => ({
      ...prev,

      sections: prev.sections.map((section) =>
        section.localId === sectionId
          ? {
              ...section,
              [field]: value,
            }
          : section
      ),
    }));
  };

  // =====================================================
  // REMOVE SECTION
  // =====================================================

  const removeSection = (sectionId: string) => {
    setChapterData((prev) => ({
      ...prev,

      sections: prev.sections.filter(
        (section) =>
          section.localId !== sectionId
      ),
    }));
  };

  // =====================================================
  // SAVE CHAPTER
  // =====================================================

  const handleSubmit = async () => {
    if (saving) return;

    if (!librarySlug || !bookSlug) {
      toast.error("Missing library or book slug!");
      return;
    }

    const chapterSlug =
      chapterData.chapterSlug.trim();

    if (
      !chapterSlug ||
      !chapterData.contentTitle.trim() ||
      chapterSlug.includes("/") ||
      chapterSlug === "." ||
      chapterSlug === ".."
    ) {
      toast.error(
        "Please enter a valid chapter title and slug!"
      );
      return;
    }

    if (chapterData.sections.length === 0) {
      toast.error(
        "Please add at least one section!"
      );
      return;
    }

    const invalidSection =
      chapterData.sections.find(
        (section) =>
          !section.sectionTitle.trim() ||
          !section.content
            .replace(/<[^>]*>/g, "")
            .replace(/&nbsp;/gi, " ")
            .trim()
      );

    if (invalidSection) {
      toast.error(
        "Every section must have a title and content!"
      );
      return;
    }

    setSaving(true);

    try {
      const chapterRef = doc(
        fireDB,
        "NewBlogs",
        librarySlug,
        "books",
        bookSlug,
        "chapters",
        chapterSlug
      );

      // Check for an existing chapter.
      const existingChapter = await getDoc(
        chapterRef
      );

      if (existingChapter.exists()) {
        toast.error(
          "This chapter already exists. Use Update Chapter instead."
        );
        return;
      }

      // Remove local React-only IDs.
      const sectionsToSave: SectionData[] =
        chapterData.sections.map(
          ({ sectionTitle, content }) => ({
            sectionTitle:
              sectionTitle.trim(),
            content,
          })
        );

      // Remove editor-only section metadata.
      const {
        sections: _editorSections,
        ...chapterFields
      } = chapterData;

      // Save sections while keeping the existing
      // Firestore chapter document structure.
      const chapterToSave = {
        ...chapterFields,

        chapterSlug,

        // Main editor removed.
        content: "",

        sections: sectionsToSave,

        time: Timestamp.now(),
      };

      console.log(
        "Saving chapter:",
        chapterToSave
      );

      await setDoc(
        chapterRef,
        chapterToSave
      );

      toast.success(
        "Chapter created successfully!"
      );

      // Reset the form after successful saving.
      setChapterData(createInitialData());

      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    } catch (error) {
      console.error(
        "Error creating chapter:",
        error
      );

      toast.error(
        "Error creating chapter!"
      );
    } finally {
      setSaving(false);
    }
  };

  // =====================================================
  // UI
  // =====================================================

  return (
    <div className="min-h-screen bg-gray-100 px-4 py-10 md:px-6">
      <div className="mx-auto max-w-5xl space-y-8">

        {/* ========================================== */}
        {/* PAGE HEADING */}
        {/* ========================================== */}

        <motion.h1
          initial={{
            opacity: 0,
            y: -10,
          }}
          animate={{
            opacity: 1,
            y: 0,
          }}
          transition={{
            duration: 0.5,
          }}
          className="mb-6 text-center text-4xl font-bold text-gray-800"
        >
          Create New Chapter
        </motion.h1>

        {/* ========================================== */}
        {/* CHAPTER INFORMATION */}
        {/* ========================================== */}

        <motion.div
          initial={{
            opacity: 0,
            scale: 0.95,
          }}
          animate={{
            opacity: 1,
            scale: 1,
          }}
          className="rounded-2xl border bg-gradient-to-r from-red-100 to-blue-100 p-6 shadow-lg"
        >
          <h2 className="mb-5 text-2xl font-semibold text-gray-800">
            Chapter Information
          </h2>

          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">

            {/* CONTENT TITLE */}

            <div>
              <label className="mb-2 block font-semibold">
                Content Title *
              </label>

              <input
                type="text"
                value={chapterData.contentTitle}
                onChange={(e) =>
                  updateField(
                    "contentTitle",
                    e.target.value
                  )
                }
                placeholder="Enter content title"
                className="w-full rounded-lg border p-3 shadow-sm focus:ring-2 focus:ring-red-400"
              />
            </div>

            {/* CHAPTER TITLE */}

            <div>
              <label className="mb-2 block font-semibold">
                Chapter Title
              </label>

              <input
                type="text"
                value={chapterData.title}
                onChange={(e) =>
                  setChapterData((prev) => ({
                    ...prev,
                    title: e.target.value,
                    titleManual: true,
                  }))
                }
                placeholder="Enter chapter title"
                className="w-full rounded-lg border p-3"
              />
            </div>

            {/* CHAPTER SLUG */}

            <div>
              <label className="mb-2 block font-semibold">
                Chapter Slug *
              </label>

              <input
                type="text"
                value={chapterData.chapterSlug}
                onChange={(e) =>
                  setChapterData((prev) => ({
                    ...prev,
                    chapterSlug: e.target.value,
                    chapterSlugManual: true,
                  }))
                }
                placeholder="chapter-slug"
                className="w-full rounded-lg border p-3 shadow-sm focus:ring-2 focus:ring-red-400"
              />
            </div>

            {/* AUTHOR */}

            <div>
              <label className="mb-2 block font-semibold">
                Author
              </label>

              <input
                type="text"
                value={chapterData.author}
                onChange={(e) =>
                  updateField(
                    "author",
                    e.target.value
                  )
                }
                placeholder="Enter author name"
                className="w-full rounded-lg border p-3 shadow-sm focus:ring-2 focus:ring-blue-400"
              />
            </div>
          </div>
        </motion.div>

        {/* ========================================== */}
        {/* SECTIONS ONLY - MAIN EDITOR REMOVED */}
        {/* ========================================== */}

        <motion.div
          initial={{
            opacity: 0,
          }}
          animate={{
            opacity: 1,
          }}
          className="space-y-6 rounded-2xl border border-yellow-200 bg-yellow-50 p-6 shadow-lg"
        >
          <div>
            <h2 className="text-3xl font-semibold text-yellow-800">
              Chapter Sections
            </h2>

            <p className="mt-2 text-sm text-gray-600">
              Add sections to build your chapter.
              Each section has its own rich text editor.
            </p>
          </div>

          <AnimatePresence initial={false}>
            {chapterData.sections.map(
              (section, index) => (
                <motion.div
                  key={section.localId}
                  initial={{
                    opacity: 0,
                    y: -10,
                    scale: 0.95,
                  }}
                  animate={{
                    opacity: 1,
                    y: 0,
                    scale: 1,
                  }}
                  exit={{
                    opacity: 0,
                    x: -50,
                    scale: 0.9,
                  }}
                  transition={{
                    duration: 0.3,
                  }}
                  className="space-y-4 rounded-2xl border bg-white p-5 shadow transition-all hover:shadow-md"
                >
                  {/* SECTION HEADER */}

                  <div className="flex items-center justify-between gap-4">
                    <h3 className="text-lg font-semibold">
                      Section {index + 1}
                    </h3>

                    <button
                      type="button"
                      onClick={() =>
                        removeSection(
                          section.localId
                        )
                      }
                      className="font-medium text-red-600 hover:text-red-800"
                    >
                      Remove
                    </button>
                  </div>

                  {/* SECTION TITLE */}

                  <div>
                    <label className="mb-2 block font-medium">
                      Section Title
                    </label>

                    <input
                      type="text"
                      placeholder="Enter section title"
                      value={section.sectionTitle}
                      onChange={(e) =>
                        updateSection(
                          section.localId,
                          "sectionTitle",
                          e.target.value
                        )
                      }
                      className="w-full rounded-lg border p-3"
                    />
                  </div>

                  {/* SECTION CKEDITOR */}

                  <div>
                    <label className="mb-2 block font-medium">
                      Section Content
                    </label>

                    <BlogEditor
                      editorId={`section-${section.localId}`}
                      initialContent={section.content}
                      onChange={(content: string) =>
                        updateSection(
                          section.localId,
                          "content",
                          content
                        )
                      }
                    />
                  </div>
                </motion.div>
              )
            )}
          </AnimatePresence>

          {/* ADD SECTION */}

          <motion.button
            type="button"
            whileTap={{
              scale: 0.93,
            }}
            whileHover={{
              scale: 1.03,
            }}
            onClick={addSection}
            className="rounded-xl bg-blue-600 px-6 py-3 text-white shadow transition-all hover:bg-blue-700"
          >
            + Add Section
          </motion.button>
        </motion.div>

        {/* ========================================== */}
        {/* SEO FIELDS */}
        {/* ========================================== */}

        <div className="space-y-4 rounded-2xl bg-gray-200 p-6 shadow">
          <h2 className="text-2xl font-semibold text-gray-800">
            SEO Settings
          </h2>

          {(
            [
              "seoTitle",
              "seoDescription",
              "seoTags",
              "seoHeading",
              "canonicalUrl",
            ] as const
          ).map((field) => (
            <div
              key={field}
              className="space-y-2"
            >
              <label className="block font-medium">
                {field === "canonicalUrl"
                  ? "Canonical URL (auto-generated)"
                  : field.replace(/seo/i, "SEO ")}
              </label>

              {field === "seoDescription" ? (
                <textarea
                  rows={4}
                  placeholder={`Enter ${field}`}
                  value={chapterData[field]}
                  onChange={(e) =>
                    updateField(
                      field,
                      e.target.value
                    )
                  }
                  className="w-full rounded-lg border p-3 shadow-sm focus:ring-2 focus:ring-gray-400"
                />
              ) : (
                <input
                  type="text"
                  placeholder={`Enter ${field}`}
                  value={chapterData[field]}
                  readOnly={
                    field === "canonicalUrl"
                  }
                  onChange={(e) =>
                    updateField(
                      field,
                      e.target.value
                    )
                  }
                  className={`w-full rounded-lg border p-3 shadow-sm focus:ring-2 focus:ring-gray-400 ${
                    field === "canonicalUrl"
                      ? "cursor-not-allowed bg-gray-100"
                      : ""
                  }`}
                />
              )}
            </div>
          ))}
        </div>

        {/* ========================================== */}
        {/* SUBMIT */}
        {/* ========================================== */}

        <motion.button
          type="button"
          whileHover={
            saving
              ? undefined
              : { scale: 1.02 }
          }
          whileTap={
            saving
              ? undefined
              : { scale: 0.95 }
          }
          onClick={handleSubmit}
          disabled={saving}
          className={`w-full rounded-lg p-4 text-xl font-semibold text-white transition-all ${
            saving
              ? "cursor-not-allowed bg-gray-500"
              : "bg-green-600 hover:bg-green-700"
          }`}
        >
          {saving
            ? "Saving Chapter..."
            : "Submit Chapter"}
        </motion.button>
      </div>

      <ToastContainer position="top-center" />
    </div>
  );
}
